import { useEffect, useMemo, useState } from 'react'
import {
  capacity,
  filterConfiguration,
  filterMap,
  FilterMapProps,
  instantBook,
  SpaceType,
  spaceTypes,
} from './configurations'
import { MlIconCard } from '../../molecules'
import { AtPillDropdown, AtToggle, BusinessHours, DropdownOption, Holiday } from '../../atoms'
import { DateRange } from 'react-day-picker'
import {
  FilterBuilding,
  buildingsArrayProps,
  regionsArrayProps,
  VIEW_ALL_REGION,
  VIEW_ALL_BUILDING,
  getBuildingOptions,
  getRegionOptions,
} from './building-options'
import { getCategoryGroupByType, areTypesInSameGroup } from './category-groups'

export type FilterState = {
  type?: SpaceType // Deprecated: use types instead. Kept for backward compatibility
  types?: SpaceType[] // Array of selected space types. Empty array means all types
  date?: DateRange | Date
  duration?: DropdownOption
  startTime?: DropdownOption
  calendarOpen?: boolean
  capacity?: DropdownOption
  instantBook?: boolean
  loading?: boolean
  disabled?: boolean
  location?: DropdownOption
  building?: DropdownOption
  businessTimes?: string[]
  isModified?: boolean
}

export * from './building-options'

export type FilterChangeDataType = { [field in keyof FilterState]: FilterState[field] }
export type FilterChangeHandler = (value: FilterChangeDataType) => void

export interface FilterProps {
  buildings: FilterBuilding[]
  state?: FilterState
  onChange?: FilterChangeHandler
  spaceLabel?: string
  withButtonSpace?: boolean
  enableAvailabilityOptions?: boolean
  isBDP?: boolean
  isFlexibleTemplate?: boolean
  disableFlexibleTMFilters?: boolean
  hasInitialRegion?: boolean
  hasInitialBuilding?: boolean
  hideSpaceTypeFilter?: boolean
  holidays?: Holiday
  businessHours?: BusinessHours
  disableRegionFilter: boolean
  disableBuildingFilter: boolean
}

// Helper function to get current types array, handling backward compatibility
const getCurrentTypes = (state?: FilterState): SpaceType[] => {
  if (state?.types && state.types.length > 0) {
    return state.types
  }
  // Backward compatibility: if type exists, convert to array
  if (state?.type) {
    return [state.type]
  }
  return []
}

// Helper function to get common capacity options across all selected types
const getCommonCapacityOptions = (types: SpaceType[]): DropdownOption[] => {
  if (types.length === 0) return []
  if (types.length === 1) return capacity[types[0]] || []

  // Get capacity options for all types
  const allCapacityOptions = types.map((type) => capacity[type] || [])

  // If any type has no capacity options, return empty array
  if (allCapacityOptions.some((options) => options.length === 0)) {
    return []
  }

  // Find common options (options that exist in all types)
  const firstTypeOptions = allCapacityOptions[0]
  return firstTypeOptions.filter((option) =>
    allCapacityOptions.every((options) => options.some((opt) => opt.value === option.value))
  )
}

// Helper function to get common filter configuration across all selected types
const getCommonFilterConfig = (
  types: SpaceType[],
  businessHours?: BusinessHours,
  state?: FilterState,
  onChange?: FilterChangeHandler,
  isBDP?: boolean
): FilterMapProps[] => {
  if (types.length === 0) return []
  if (types.length === 1) {
    const config = filterConfiguration(businessHours, state, onChange, isBDP)
    return config[types[0]] || []
  }

  // Get filter configs for all types
  const config = filterConfiguration(businessHours, state, onChange, isBDP)
  const allConfigs = types.map((type) => config[type] || [])

  // Find common filter IDs (filters that exist in all types)
  const firstTypeConfig = allConfigs[0]
  const commonFilterIds = firstTypeConfig
    .map((filter) => filter.id)
    .filter((id) => allConfigs.every((configs) => configs.some((f) => f.id === id)))

  // Return filters that are common to all types
  return firstTypeConfig.filter((filter) => commonFilterIds.includes(filter.id))
}

export const Filter = ({
  onChange,
  spaceLabel = 'Space Type',
  state,
  withButtonSpace,
  buildings,
  isBDP,
  isFlexibleTemplate,
  hideSpaceTypeFilter,
  holidays,
  businessHours,
  disableRegionFilter,
  disableBuildingFilter,
}: FilterProps) => {
  const [regionDropdownList, setRegionDropdownList] = useState<regionsArrayProps[]>([])
  const [locationQueryDropdownList, setLocationQueryDropdownList] = useState<buildingsArrayProps[]>([])
  const [isDropdownDisabled, setIsDropdownDisabled] = useState(false)
  let isBDPReferrer = false
  if (typeof window !== 'undefined') {
    const urlParams = new URLSearchParams(window.location.search)
    const buildingParam = urlParams.get('building')
    isBDPReferrer = buildingParam !== null
  }

  const currentTypes = useMemo(() => getCurrentTypes(state), [state?.types, state?.type])

  const commonCapacityOptions = useMemo(() => getCommonCapacityOptions(currentTypes), [currentTypes])

  // Memoize common filter configuration across all selected types
  const filterConfig = useMemo(
    () => getCommonFilterConfig(currentTypes, businessHours, state, onChange, isBDP),
    [currentTypes, businessHours, state, onChange, isBDP]
  )

  // Determine if capacity should be shown
  // Hide capacity if ONLY suite or ONLY day_pass is selected (as per previous logic)
  // If suite/day_pass is combined with other types, evaluate normally
  const shouldShowCapacity = useMemo(() => {
    if (currentTypes.length === 0) return false

    // Hide capacity if only suite or only day_pass is selected
    if (currentTypes.length === 1) {
      const singleType = currentTypes[0]
      if (singleType === 'suite' || singleType === 'day_pass') {
        return false
      }
    }

    // For multiple types or single type (not suite/day_pass), check if all have capacity options
    return currentTypes.every((type) => capacity[type] && capacity[type].length > 0)
  }, [currentTypes])

  const populateBuildingsListFromRegion = (region: string) => {
    if (!region || region === VIEW_ALL_REGION.id) {
      setLocationQueryDropdownList([])
      return
    }

    setLocationQueryDropdownList(getBuildingOptions(buildings, region))
  }

  useEffect(() => {
    setRegionDropdownList(getRegionOptions(buildings))
  }, [])

  useEffect(() => {
    // update list
    populateBuildingsListFromRegion(state?.location?.value ?? 'new-york')
  }, [state?.location?.value])

  useEffect(() => {
    isBDP && setIsDropdownDisabled(true)
  }, [])

  return (
    <div className="flex flex-col">
      {hideSpaceTypeFilter ? null : (
        <>
          <p className="px-4 lg:px-0 mb-1 text-neutrals-800 leading-1.5 font-bold">{spaceLabel}</p>
          <div className="p-1.5 px-4 lg:px-0 mb-10 flex gap-8 overflow-x-auto lg:overflow-x-visible lg:justify-evenly">
            {spaceTypes.map((space, i) => {
              const isSelected = currentTypes.includes(space.typeKey)
              const clickedTypeGroup = getCategoryGroupByType(space.typeKey)

              return (
                <MlIconCard
                  key={i}
                  {...space}
                  onClick={() => {
                    if (isSelected) {
                      // If selected, remove it
                      const newTypes = currentTypes.filter((t) => t !== space.typeKey)
                      onChange?.({ types: newTypes })
                    } else {
                      // If not selected, check if there are types from a different group
                      const typesFromOtherGroups = currentTypes.filter((t) => !areTypesInSameGroup(t, space.typeKey))

                      if (typesFromOtherGroups.length > 0) {
                        // If there are types from other groups, replace them with the new type's group
                        const newTypeGroup = clickedTypeGroup
                        if (newTypeGroup) {
                          // Start with the clicked type and add other types from the same group if they were selected
                          const typesFromSameGroup = currentTypes.filter((t) => areTypesInSameGroup(t, space.typeKey))
                          const newTypes = [space.typeKey, ...typesFromSameGroup]
                          onChange?.({ types: newTypes })
                        } else {
                          // If type doesn't belong to any group, just add it
                          onChange?.({ types: [space.typeKey] })
                        }
                      } else {
                        // No types from other groups, just add the new type
                        const newTypes = [...currentTypes, space.typeKey]
                        onChange?.({ types: newTypes })
                      }
                    }
                  }}
                  selected={isSelected}
                  disabled={state?.loading}
                />
              )
            })}
          </div>
        </>
      )}
      <div>
        <div className="flex gap-4 px-4 lg:px-0 pb-4 md:pb-0 overflow-x-auto lg:overflow-x-visible">
          <AtPillDropdown
            id="or-filter-section-region-dropdown"
            label="New York"
            options={regionDropdownList}
            value={state?.location}
            onChange={(option) => {
              onChange?.({
                location: option,
                // if location changes, the previous building is no longer valid
                ...(state?.location?.value !== option.value ? { building: { value: '', label: '' } } : {}),
              })
            }}
            disabled={
              (isDropdownDisabled && !isBDPReferrer) ||
              (state?.loading && state?.building?.value !== '') ||
              disableRegionFilter ||
              isBDP
            }
            className="min-w-[10.5rem]"
          />
          <AtPillDropdown
            id="or-filter-section-buildings-dropdown"
            label="Buildings"
            options={locationQueryDropdownList}
            value={state?.building}
            onChange={(option) => onChange?.({ building: option })}
            disabled={
              (isDropdownDisabled && !isBDPReferrer) ||
              (state?.loading && state?.building?.value !== '') ||
              (disableRegionFilter && disableBuildingFilter) ||
              isBDP ||
              locationQueryDropdownList.length === 0
            }
          />
          {shouldShowCapacity && commonCapacityOptions.length > 0 && (
            <AtPillDropdown
              id="or-filter-section-capacity-dropdown"
              options={commonCapacityOptions}
              label="Capacity"
              value={state?.capacity}
              disabled={state?.loading}
              onChange={(option) => onChange?.({ capacity: option })}
            />
          )}
          <div className="ml-auto hidden md:block">
            <AtToggle
              {...instantBook}
              id="or-filter-section-instant-book-switch-desktop"
              checked={state?.instantBook}
              disabled={state?.loading}
              onChange={(instantBook) => onChange?.({ instantBook })}
            />
            {withButtonSpace && <div aria-hidden className="hidden lg:block grow" />}
          </div>
        </div>
        <div className="block mt-4 md:mt-8 px-4 lg:px-0 md:hidden">
          <AtToggle
            {...instantBook}
            id="or-filter-section-instant-book-switch-mobile"
            checked={state?.instantBook}
            onChange={(instantBook) => onChange?.({ instantBook })}
          />
          {withButtonSpace && <div aria-hidden className="hidden lg:block grow" />}
        </div>
      </div>
      {filterConfig.length > 0 && (
        <>
          <div aria-hidden className="h-[1px] border-t-1 border-neutrals-400 block my-10 md:mx-4 lg:mx-0" />
          <p className="px-4 lg:px-0 mb-1 text-neutrals-800 leading-1.5 font-bold">Availability</p>
          <p className="text-neutrals-800 text-14 mb-3 px-4 lg:px-0">
            Select a building and booking date to check availability.
          </p>
          <div
            className={`px-4 lg:px-0 lg:pl-1 overflow-x-scroll lg:overflow-x-visible flex h-14 items-center gap-4 ${
              !withButtonSpace ? 'justify-between' : ''
            }`}
          >
            <div>
              <div className="flex gap-4">{filterConfig.map((props, index) => filterMap(props, index, holidays))}</div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
